import { Add, Delete, Edit, RecentActors } from '@mui/icons-material';
import { ButtonGroup, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Button as SMButton,
  Loader,
  Page,
  PageTitleWithButton,
  Table,
  Toast
} from '../../../components';
import { AdminContext } from '../../../context/admin';
import {
  deleteAdminAttorneyOffice,
  getAdminAttorneyOffices,
  getAdminAttorneyOfficeUsers
} from '../../../fetch';
import { IAttorneyOffice, IAttorneyOfficeUser } from '../../../models';
import { AttorneyOfficeModal } from './AttorneyOfficeModal';
import { AttorneyOfficeUserModal } from './AttorneyOfficeUserModal';
import { MobileAttorneyOffice } from './MobileAttorneyOffice';

export const ManageAttorneyOffices = () => {
  const classes = useStyles();
  const history = useHistory();
  const { setSelectedUsersTab } = useContext(AdminContext);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isError, showError] = useState<boolean>(false);
  const [isLoadingAttorneys, setLoadingAttorneys] = useState<boolean>(true);
  const [attorneys, setAttorneys] = useState<IAttorneyOffice[]>([]);
  const [attorneyOfficeUsers, setAttorneyOfficeUsers] = useState<IAttorneyOfficeUser[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isDeleting, setDeleting] = useState<boolean>(false);
  const [currentAttorneyOffice, setCurrentAttorneyOffice] = useState<IAttorneyOffice | null>(null);
  const [isModalOpen, showModal] = useState<boolean>(false);
  const [isUserModalOpen, showUserModal] = useState<boolean>(false);
  const [isLoadingUsers, setLoadingUsers] = useState<boolean>(false);

  const isMobile = useMediaQuery('(max-width: 960px)');

  const fetchAttorneyOffices = async () => {
    try {
      setLoadingAttorneys(true);
      const res = await getAdminAttorneyOffices();
      setAttorneys(res);
    } catch (error) {
      setErrorMessage('Error loading attorney offices, please refresh and try again.');
      console.log(error);
    } finally {
      setLoadingAttorneys(false);
    }
  };

  useEffect(() => {
    fetchAttorneyOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async id => {
    const result = window.confirm('Are you sure you want to delete this attorney office?');
    if (result) {
      setDeleting(true);
      try {
        await deleteAdminAttorneyOffice(id);
        fetchAttorneyOffices();
        setSuccessMessage('Attorney Office Deleted!');
      } catch (error) {
        var potentialErrorMessage = error?.response?.data?.Detail;
        if (potentialErrorMessage) {
          setErrorMessage(potentialErrorMessage);
        } else {
          setErrorMessage('Unable to delete attorney office.');
        }
        showError(true);
      } finally {
        setDeleting(false);
      }
    }
  };

  const handleEdit = (attorneyOffice: IAttorneyOffice) => {
    setCurrentAttorneyOffice(attorneyOffice);
    showModal(true);
  };

  const fetchAttorneyOfficeUsers = async (id: number) => {
    try {
      setLoadingUsers(true);
      const res = await getAdminAttorneyOfficeUsers(id);
      setAttorneyOfficeUsers(res);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleEditUsers = async (attorneyOffice: IAttorneyOffice) => {
    showUserModal(true);
    fetchAttorneyOfficeUsers(attorneyOffice.attorneyOfficeId);
    setCurrentAttorneyOffice(attorneyOffice);
  };

  const columns = [
    {
      Header: 'Workplace',
      accessor: 'workplace',
      sort: false
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      className: classes.editButton,
      sort: false,

      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: IAttorneyOffice } };
      }) => {
        return (
          <>
            <SMButton
              id="edit"
              color="secondary"
              startIcon={<RecentActors />}
              onClick={async () => {
                handleEditUsers(original);
              }}
            >
              Edit Attorney Users
            </SMButton>
            <SMButton
              id="edit"
              color="primary"
              startIcon={<Edit />}
              onClick={async () => {
                handleEdit(original);
              }}
            >
              Edit
            </SMButton>
            <SMButton
              id="delete"
              className={classes.deleteButton}
              startIcon={<Delete />}
              onClick={() => {
                handleDelete(original.attorneyOfficeId);
              }}
            >
              Delete
            </SMButton>
          </>
        );
      }
    }
  ];

  return (
    <>
      {isDeleting && (
        <Loader position="centered" type="fullscreen">
          Deleting...
        </Loader>
      )}
      <Page title="Manage Attorney Offices">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'Manage Attorney Offices'
          }}
          button={
            <ButtonGroup>
              <SMButton
                id="create-user"
                startIcon={<Add />}
                onClick={() => showModal(true)}
                color="primary"
                variant="contained"
                className={classes.addUserButton}
              >
                Add Attorney Office
              </SMButton>
              <SMButton
                id="create-attorney-user"
                startIcon={<Add />}
                onClick={() => {
                  // Select the External users tab before navigating there
                  setSelectedUsersTab(1);
                  history.push('/admin/user-tools/users?personType=Attorney');
                }}
                color="primary"
                variant="contained"
                className={classes.addUserButton}
              >
                Add Attorney Users
              </SMButton>
            </ButtonGroup>
          }
        />
        <Table
          columns={columns}
          data={attorneys}
          isLoading={isLoadingAttorneys}
          hidePagination
          ResponsiveComponent={isMobile ? MobileAttorneyOffice : undefined}
          mobileProps={{
            handleEdit,
            handleDelete,
            handleEditUsers
          }}
        />
        <Toast
          id="attorney-offices-error"
          autoHideDuration={5000}
          message={errorMessage}
          open={isError}
          onClose={() => showError(false)}
          variant="error"
        />
        <Toast
          id="attorney-offices-success"
          message={successMessage}
          open={!!successMessage}
          onClose={() => setSuccessMessage('')}
          variant="success"
        />
      </Page>
      <AttorneyOfficeModal
        open={isModalOpen}
        onClose={() => {
          showModal(false);
          setTimeout(() => {
            setCurrentAttorneyOffice(null);
          }, 500);
        }}
        onSave={() => fetchAttorneyOffices()}
        currentAttorneyOffice={currentAttorneyOffice}
      />
      <AttorneyOfficeUserModal
        open={isUserModalOpen}
        isLoadingUsers={isLoadingUsers}
        onClose={() => {
          showUserModal(false);
          setTimeout(() => {
            setCurrentAttorneyOffice(null);
          }, 500);
        }}
        users={attorneyOfficeUsers}
        currentAttorneyOffice={currentAttorneyOffice}
        fetchAttorneyOfficeUsers={fetchAttorneyOfficeUsers}
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  addUserWrapper: {
    marginBottom: theme.spacing(1)
  },
  addUserButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
